// What the SPA does when the BFF says the session is gone (DMS-10: every
// `/api/*` call is behind the session gate, so a 401 there means "log in
// again", never "you lack permission"). One place for the redirect so the
// axios interceptor and the raw `fetch`/`<img>` call sites behave the same.
import { AppRoutes } from "../components/app/routes";
import { fetchSession } from "./auth-client";

/** Query parameter the login screen reads to send the user back after login. */
export const FROM_PARAM = "from";

/**
 * Only a path on this same origin is a valid return target (open-redirect
 * guard); anything else means "go to the root". Validated by parsing, not by
 * prefix: `//evil.example` is protocol-relative, and browsers normalise
 * `/\evil.example` to the same thing, so a `startsWith("//")` check is not
 * enough. Same rule as `safeFrom` in the BFF (packages/bff/src/auth/routes.ts).
 */
export const safeReturnPath = (
  value: string | null | undefined,
): string | null => {
  if (typeof value !== "string" || !value.startsWith("/")) return null;
  if (typeof window === "undefined") return null;
  const origin = window.location.origin;
  let url: URL;
  try {
    url = new URL(value, origin);
  } catch {
    return null;
  }
  if (url.origin !== origin) return null;
  return url.pathname + url.search;
};

/**
 * Thrown by a repository when the backend answered 401 and the redirect to
 * login has already been triggered. Callers skip their generic error toast
 * for it — the screen is about to be replaced by the login anyway.
 */
export class SessionExpiredError extends Error {
  constructor() {
    super("session expired");
    this.name = "SessionExpiredError";
  }
}

/** True for a SessionExpiredError, also after Redux serialised it (name only). */
export const isSessionExpiredError = (error: unknown): boolean =>
  (error as { name?: unknown } | null)?.name === "SessionExpiredError";

/**
 * Full-page navigation to the login screen, remembering where the user was so
 * the login can send them back (`Login` reads `?from=`). A no-op on the login
 * screen itself (no loop) and outside a browser (tests, SSR).
 */
export const redirectToLogin = (): void => {
  if (typeof window === "undefined") return;
  if (window.location.pathname === AppRoutes.LOGIN) return;
  const from = window.location.pathname + window.location.search;
  window.location.assign(
    `${AppRoutes.LOGIN}?${FROM_PARAM}=${encodeURIComponent(from)}`,
  );
};

/**
 * For a response whose status is known: redirects when it is a 401 and
 * returns true so the caller can stop there instead of showing an error.
 */
export const redirectIfUnauthorized = (status: number): boolean => {
  if (status !== 401) return false;
  redirectToLogin();
  return true;
};

/**
 * For failures that carry no status — an `<img src="/api/...">` that failed
 * to load. Asks the BFF whether the session still exists and redirects when
 * it does not. A network error is not a lost session, so it stays put.
 */
export const redirectIfSessionGone = (): Promise<boolean> => {
  // Every broken <img> on a screen calls this at once (a Compliance case has
  // one thumbnail per capture); one /auth/me and one redirect are enough.
  if (!sessionCheck) {
    sessionCheck = checkSession().finally(() => {
      sessionCheck = null;
    });
  }
  return sessionCheck;
};

let sessionCheck: Promise<boolean> | null = null;

const checkSession = async (): Promise<boolean> => {
  let user;
  try {
    user = await fetchSession();
  } catch {
    return false;
  }
  if (user) return false;
  redirectToLogin();
  return true;
};
